import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useDceUploadStatus } from '../../hooks';
import {
  DCE_UPLOAD_STATUS_LABELS,
  type DceUploadStatus,
} from '../../types/dce-upload.types';

interface DceUploadProgressProps {
  /** null tant que le presign n'a pas répondu — évite un polling parasite sur uploadId=0. */
  uploadId: number | null;
  /** Progression cumulative de la phase upload navigateur (0..1), avant le pipeline back. */
  uploadPhaseRatio?: number;
  /** Phase actuelle ('uploading' avant confirm, 'processing' après). */
  phase: 'uploading' | 'processing' | 'idle';
}

const STATUS_BADGE: Record<DceUploadStatus, { variant: 'default' | 'destructive' | 'outline' | 'secondary'; tone: string }> = {
  PENDING: { variant: 'outline', tone: 'text-slate-500' },
  UNZIPPING: { variant: 'secondary', tone: 'text-blue-600' },
  CLASSIFYING: { variant: 'secondary', tone: 'text-violet-600' },
  EXTRACTING: { variant: 'secondary', tone: 'text-amber-600' },
  INDEXING: { variant: 'secondary', tone: 'text-cyan-600' },
  DONE: { variant: 'default', tone: 'text-emerald-600' },
  PARTIAL_SUCCESS: { variant: 'default', tone: 'text-amber-600' },
  FAILED: { variant: 'destructive', tone: 'text-red-600' },
};

export function DceUploadProgress({
  uploadId,
  uploadPhaseRatio,
  phase,
}: DceUploadProgressProps) {
  const { data, isLoading } = useDceUploadStatus(uploadId);

  if (phase === 'uploading') {
    const percent = Math.round((uploadPhaseRatio ?? 0) * 100);
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>Upload vers le stockage…</span>
          <span className="text-muted-foreground">{percent}%</span>
        </div>
        <Progress value={percent} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>Préparation…</span>
        </div>
        <Progress value={0} />
      </div>
    );
  }

  const badge = STATUS_BADGE[data.status];
  // Pas de % pour la phase processing : c'est une cascade de jobs (Sprint 1
  // → unzip seul). On affiche le statut textuel + une barre indéterminée.
  const isTerminal =
    data.status === 'DONE' ||
    data.status === 'PARTIAL_SUCCESS' ||
    data.status === 'FAILED';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className={badge.tone}>{DCE_UPLOAD_STATUS_LABELS[data.status]}</span>
        <Badge variant={badge.variant}>{data.status}</Badge>
      </div>
      <Progress value={isTerminal ? 100 : undefined} />
      {data.fileCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {data.fileCount} fichier{data.fileCount > 1 ? 's' : ''} importé
          {data.fileCount > 1 ? 's' : ''}
          {data.failedCount > 0 && (
            <span className="text-amber-600">
              {' '}
              · {data.failedCount} en erreur
            </span>
          )}
        </p>
      )}
      {data.errorMessage && (
        <p className="text-xs text-red-600">{data.errorMessage}</p>
      )}
    </div>
  );
}
